import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import useShots from '../../contexts/shots/provider';
import { editShot, bulkEditShots } from '../../stores/actions/shots';
import { GenerateAllShotThumbnails } from '../../connection/shots';
import type { GraphicInterface } from '../../stores/types/graphics';
import type { Shot } from '../../stores/types/shots';
import { GreenroomState } from '../../stores/types/types';
import { maxOverlayAlertMs } from '../../types/constants';
import { getDimensionsOfGraphic } from '../../utils/custom-graphics';
import { getMediaDimensionsGivenUrl } from '../../utils/media';
import { arrayMove, isVideo } from '../../utils/utils';
import * as Alerts from '../general-components/alert-service';
import { COLORS, ICONS } from '../general-components/icon';
import RightClickMenu, { MenuItem } from '../general-components/right-click-menu';

const filterHidden = (i: MenuItem) => !i.hidden;
interface Props {
	socket: Socket | null;
	clickCoordinates?: [number, number];
	contextMenuId: string;
	selectedGraphic: GraphicInterface;
	shot: Shot | null;
	isForShotEditorModal?: boolean;
	renderedGraphics: Array<number>;
	handleRemoveGraphic: () => void;
	handleSaveAndUpdate: (newGraphic: GraphicInterface) => void;
}

export default function GraphicsContextMenu(props: Props) {
	const {
		clickCoordinates,
		contextMenuId,
		socket,
		selectedGraphic,
		shot,
		isForShotEditorModal,
		renderedGraphics,
		handleRemoveGraphic,
		handleSaveAndUpdate,
	} = props;
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const useNewShotThumbs = useSelector((state: GreenroomState) => state.SettingsReducer.featureFlags.use_new_shot_thumbs);
	const { editShotModalShot, isShotBuilderOpen, otherShotGraphicsIsMax } = useShots();
	const dispatch = useDispatch();

	const handleForwardBackward = (forward: boolean) => {
		if (!token || !shot || !selectedGraphic) return;

		const currentGraphicIndex = renderedGraphics.findIndex((num: number) => num === selectedGraphic.id);
		const targetGraphicIndex = currentGraphicIndex + (forward ? 1 : -1);

		if (targetGraphicIndex >= 0 && targetGraphicIndex <= renderedGraphics.length - 1) {
			let newGraphicArray = arrayMove(renderedGraphics, currentGraphicIndex, targetGraphicIndex);

			if (currentGraphicIndex === -1) {
				// probably a global graphic
				if (forward) {
					newGraphicArray.push(selectedGraphic.id);
				} else {
					newGraphicArray = [selectedGraphic.id, ...newGraphicArray];
				}
			}

			// replace shotGraphics with the reordered array
			const editedShot: Shot = JSON.parse(JSON.stringify(shot));
			editedShot.shot_data.shotGraphics = newGraphicArray;

			isForShotEditorModal
				? editShotModalShot(editedShot, socket)
				: dispatch(editShot(token, editedShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
		}
	};

	const handleMaximize = async (): Promise<void> => {
		if (token) {
			const selectedGraphicUrl = selectedGraphic?.images;

			if (selectedGraphicUrl) {
				const dimensionsInPx = await getMediaDimensionsGivenUrl(selectedGraphicUrl);
				const fitToCanvas = getDimensionsOfGraphic(dimensionsInPx, 100, 100);
				const newGraphic: GraphicInterface = {
					...selectedGraphic,
					scale_x: fitToCanvas.xScale,
					scale_y: fitToCanvas.yScale,
					pos_x: 50 - fitToCanvas.xScale / 2,
					pos_y: 50 - fitToCanvas.yScale / 2,
				};

				handleSaveAndUpdate(newGraphic);
			}
		}
	};

	const handleShowInAllShots = () => {
		if (otherShotGraphicsIsMax) {
			Alerts.showAlert({
				message: 'Cannot add to all shots',
				description: "You've reached the maximum number of overlays for one or more shots",
				duration: maxOverlayAlertMs,
			});

			return;
		}

		if (selectedGraphic) {
			const newGraphic: GraphicInterface = {
				...selectedGraphic,
				is_global: true,
			};

			handleSaveAndUpdate(newGraphic);
		}
	};

	const handleRemoveGraphicFromAllShots = () => {
		if (selectedGraphic && token) {
			const newGraphic: GraphicInterface = {
				...selectedGraphic,
				is_global: false,
			};

			// now to remove from rest of shots... don't worry on save in edit module this gets handled there
			if (!isShotBuilderOpen) {
				const shotsContainingGraphic = shots.filter((s) =>
					s.shot_data.shotGraphics?.includes(selectedGraphic?.id ?? 0)
				);
				const editedShots = shotsContainingGraphic.map((originalShot) => {
					const editedShot: Shot = JSON.parse(JSON.stringify(originalShot));
					editedShot.shot_data.shotGraphics = editedShot.shot_data.shotGraphics?.filter(
						(graphicId: number) => graphicId !== selectedGraphic.id
					);
					return editedShot;
				});
				if (editedShots.length > 0) {
					dispatch(bulkEditShots(token, editedShots));
				}

				// Global overlays render on all shots regardless of shotGraphics membership,
				// so all shot thumbnails need to be regenerated when one is removed globally.
				if (useNewShotThumbs && meeting?.meeting_id && meeting?.channel && meeting?.meeting) {
					GenerateAllShotThumbnails(
						token,
						meeting.meeting_id,
						meeting.channel,
						meeting.meeting,
					).catch(console.error);
				}
			}

			// first
			handleSaveAndUpdate(newGraphic);

			// remove from current shot if it exists
			handleRemoveGraphic();
		}
	};

	const menuItems: MenuItem[] = useMemo(() => {
		return [
		{
			handleClick: () => handleForwardBackward(true),
			iconName: ICONS.FORWARD,
			text: 'Move forward',
		},
		{
			handleClick: () => handleForwardBackward(false),
			iconName: ICONS.BACKWARD,
			text: 'Move backward',
		},
		{
			handleClick: handleMaximize,
			iconName: ICONS.FULLSCREEN,
			text: 'Maximize',
		},
		{
			handleClick: handleShowInAllShots,
			hidden: !!selectedGraphic.is_global,
			iconName: ICONS.ADD,
			text: 'Add to all shots',
		},
		{
			checkboxValue: typeof selectedGraphic.play_audio !== 'number' || Boolean(selectedGraphic?.play_audio),
			handleClick: () => {
				const newGraphic = {
					...selectedGraphic,
					play_audio: selectedGraphic.play_audio === 0 ? 1 : 0,
				};
				handleSaveAndUpdate(newGraphic);
			},
			hidden: !isVideo(selectedGraphic.images),
			text: 'Audio',
		},
		{
			checkboxValue: typeof selectedGraphic.loops === 'number' && Boolean(selectedGraphic?.loops),
			handleClick: () => {
				const newGraphic = {
					...selectedGraphic,
					loops: selectedGraphic.loops === 1 ? 0 : 1,
				};
				handleSaveAndUpdate(newGraphic);
			},
			hidden: !isVideo(selectedGraphic.images),
			text: 'Loop',
		},
		{
			color: COLORS.RED,
			hidden: !!selectedGraphic.is_global,
			iconName: ICONS.TRASH,
			subMenuItems: [
				{
					color: COLORS.RED,
					handleClick: handleRemoveGraphic,
					text: 'This shot',
				},
				{
					color: COLORS.RED,
					handleClick: handleRemoveGraphicFromAllShots,
					text: 'All shots',
				},
			],
			text: 'Remove from',
		},
		{
			color: COLORS.RED,
			handleClick: handleRemoveGraphicFromAllShots,
			hidden: !selectedGraphic.is_global,
			iconName: ICONS.TRASH,
			text: 'Remove from all shots',
		},
		];
	}, [selectedGraphic]);

	const nClickCoordinates = useMemo((): [number, number] | undefined => {
		if (!clickCoordinates) return clickCoordinates;
		const shownItems = menuItems.filter(filterHidden);
		const newY = 36 * (shownItems.length);
		return [clickCoordinates[0] - 4, clickCoordinates[1] + newY]
	}, [clickCoordinates, menuItems])

	return <RightClickMenu contextMenuId={contextMenuId} items={menuItems} clickCoordinates={nClickCoordinates} />;
}
